import React from "react";
import { useHistory } from "react-router-dom";
import { Box, Breadcrumbs, Button, Chip, CircularProgress, Link, Tooltip, Typography, alpha } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import { AuditStatusChip } from "../AuditHub/components/AuditCells";
import { daysRemainingTone, formatDaysRemaining, formatShortDate, scorePercent } from "../AuditHub/utils";
import { AH_ACCENT, AH_MUTED, AH_TEXT, MetaSeparator, backButtonSx } from "../AuditHub/theme";
import { frameworkMatches } from "./ControlAuditPanel";

const DUE_COLOR = { overdue: "#dc2626", warn: "#d97706" };

const factSx = { fontSize: 13, color: AH_MUTED, flexShrink: 0, whiteSpace: "nowrap" };

const chipSx = (bg, fg, ring) => ({
  height: 22,
  flexShrink: 0,
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: "6px",
  backgroundColor: bg,
  color: fg,
  "& .MuiChip-icon": { color: "inherit", fontSize: 15, ml: "6px", mr: "-2px" },
  "& .MuiChip-label": { px: 1 },
  "&:focus-visible": { outline: `2px solid ${ring}`, outlineOffset: 1 },
});

const ownerChipSx = chipSx("#e3f2fd", "#1565c0", "#90caf9");
const nameChipSx = chipSx(alpha(AH_ACCENT, 0.12), "#c2410c", alpha(AH_ACCENT, 0.5));

const scoreLabel = (value) => {
  const percent = scorePercent(value);
  if (percent == null) return "Scan in progress";
  return `${Math.round(percent)}%`;
};

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/** Account option state for the current framework: the view needs that account's scope and a finished run. */
const accountAvailability = (account, compliance) => {
  const scope = (account.scopes || []).find((item) => frameworkMatches(compliance, item.framework));
  if (!scope) return { scope: null, reason: "Framework not assessed on this account", pendingScan: false };
  if (!scope.latestRunId) return { scope, reason: "Pending Automated Scan Completion", pendingScan: true };
  return { scope, reason: "", pendingScan: false };
};

/** One row: breadcrumb, audit name and summary, and Back - in the Audit Hub style. */
const AuditContextBar = ({ auditId, state }) => {
  const history = useHistory();

  if (!auditId) return null;

  const audit = state?.audit;
  const accounts = Array.isArray(audit?.accounts) ? audit.accounts : [];
  const accountCount = audit?.accountCount ?? accounts.length;
  const loading = state?.status === "loading" || state?.status === "idle";
  const goToAuditHub = () => history.push("/audit-hub");

  const assessmentName = audit?.name || "Assessment";
  const ownerTip = audit?.ownerEmail || "No owner assigned";
  const remaining = audit?.dueDate ? formatDaysRemaining(audit) : "—";
  const facts = [
    { key: "score", text: `Score ${scoreLabel(audit?.auditScore)}` },
    { key: "accounts", text: plural(accountCount, "account") },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        columnGap: 1.5,
        rowGap: 1,
        flexWrap: { xs: "wrap", md: "nowrap" },
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          columnGap: 1.25,
          rowGap: 0.5,
          flexWrap: "wrap",
          minWidth: 0,
          flex: "1 1 auto",
        }}
      >
        <Breadcrumbs sx={{ fontSize: 13, color: AH_MUTED, flexShrink: 0 }}>
          <Link component="button" underline="hover" color="inherit" onClick={goToAuditHub} sx={{ fontSize: 13 }}>
            Assessments
          </Link>
          <Typography sx={{ fontSize: 13, color: AH_TEXT }}>{audit?.auditCode || auditId}</Typography>
        </Breadcrumbs>
        <MetaSeparator hideOnMobile />
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <CircularProgress size={14} />
            <Typography noWrap sx={{ fontSize: 13, color: AH_MUTED }}>Loading assessment…</Typography>
          </Box>
        ) : state?.status === "error" ? (
          <Typography sx={{ fontSize: 13, color: "#b91c1c", minWidth: 0, overflowWrap: "anywhere" }}>{state.error}</Typography>
        ) : (
          <>
            <Tooltip title={assessmentName} arrow placement="bottom">
              <Chip
                icon={<AssignmentOutlinedIcon />}
                label="Assessment Name"
                size="small"
                tabIndex={0}
                aria-label={`Assessment Name: ${assessmentName}`}
                sx={nameChipSx}
              />
            </Tooltip>
            {audit?.status ? (
              <>
                <MetaSeparator />
                <Box sx={{ flexShrink: 0, display: "flex" }}>
                  <AuditStatusChip status={audit.status} />
                </Box>
              </>
            ) : null}
            <MetaSeparator />
            <Tooltip title={ownerTip} arrow placement="bottom">
              <Chip
                icon={<PersonOutlineRoundedIcon />}
                label="Owner"
                size="small"
                tabIndex={0}
                aria-label={`Owner: ${ownerTip}`}
                sx={ownerChipSx}
              />
            </Tooltip>
            <MetaSeparator />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                columnGap: 1.25,
                rowGap: 0.5,
                flexWrap: { xs: "wrap", sm: "nowrap" },
                flexShrink: 0,
                maxWidth: "100%",
              }}
            >
              {facts.map((item, index) => (
                <React.Fragment key={item.key}>
                  {index > 0 ? <MetaSeparator /> : null}
                  <Typography sx={factSx}>{item.text}</Typography>
                </React.Fragment>
              ))}
              {audit?.dueDate ? (
                <>
                  <MetaSeparator />
                  <Typography sx={factSx}>
                    Due Date: {formatShortDate(audit.dueDate)}
                    {remaining !== "—" ? (
                      <>
                        {" ("}
                        <Box
                          component="span"
                          sx={{ color: DUE_COLOR[daysRemainingTone(audit)] || "inherit", fontWeight: 600 }}
                        >
                          {remaining}
                        </Box>
                        {")"}
                      </>
                    ) : null}
                  </Typography>
                </>
              ) : null}
            </Box>
          </>
        )}
      </Box>
      <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={goToAuditHub} sx={backButtonSx}>
        Back to Audit Hub
      </Button>
    </Box>
  );
};

export { accountAvailability };
export default AuditContextBar;
